import { IAddress } from 'core/api/address/address-api-interface';
import {
  AppointmentType,
  getAppointmentTypeColor,
  getAppointmentTypeNameFromKey,
  getAppointmentTypePin,
} from 'core/constants/appointment-type';
import { createAddressString } from 'core/utilities/address-string-builder';
import { useCallback, useEffect, useState } from 'react';
import SharedDialogBase from 'shared/components/dialog-base/dialog-base';
import RouteMap from 'shared/components/route-map/route-map';

interface ITimelinePreviewDialogAppointment {
  address: IAddress;
  customerName: string;
  uid: string;
  type: string;
  start: string;
  end: string;
}

interface ITimelinePreviewDialog {
  appointments: ITimelinePreviewDialogAppointment[];
  assigneeAddress: google.maps.LatLngLiteral;
}

interface ITimelineWaypoint {
  isHome: boolean;
  appointmentDetail?: ITimelinePreviewDialogAppointment;
  nextJourney?: google.maps.DirectionsLeg;
}

const TimelinePreviewDialog = ({ appointments, assigneeAddress }: ITimelinePreviewDialog) => {
  const [loading, setLoading] = useState(true);
  const [timeline, setTimeline] = useState<ITimelineWaypoint[]>();
  const [directionResult, setDirectionResult] = useState<google.maps.DirectionsResult>();
  const [totalMilage, setTotalMileage] = useState('0');

  const getAppointmentAddressLocation = (app: ITimelinePreviewDialogAppointment) => {
    switch (app.type) {
      case AppointmentType.SHOP_FITTING:
      case AppointmentType.SHOP_SERVICE:
      case AppointmentType.SHOP_TEST:
      case AppointmentType.SHOP_WAX_REMOVAL:
        return {
          location: {
            lat: 52.912296,
            lng: -1.45606,
          },
        };
      default:
        return {
          location: {
            lat: app.address.latitude,
            lng: app.address.longitude,
          },
        };
    }
  };

  const generateGoogleDirections = useCallback(async () => {
    const directionsService = new google.maps.DirectionsService();
    const result = await directionsService.route({
      origin: assigneeAddress,
      destination: assigneeAddress,
      waypoints: appointments.map((app) => getAppointmentAddressLocation(app)),
      travelMode: google.maps.TravelMode.DRIVING,
      unitSystem: google.maps.UnitSystem.IMPERIAL,
    });
    setDirectionResult(result);
    const legs = result.routes[0].legs;
    let totalMeters = 0;
    legs.forEach((leg) => (totalMeters += leg.distance?.value ?? 0));
    setTotalMileage(`${(totalMeters * 0.000621371).toFixed(2)} mi`);

    setTimeline([
      { isHome: true, nextJourney: legs[0] },
      ...appointments.map((app, index) => ({
        isHome: false,
        appointmentDetail: app,
        nextJourney: legs[index + 1],
      })),
      { isHome: true },
    ]);
    setLoading(false);
  }, [appointments, assigneeAddress]);

  useEffect(() => {
    generateGoogleDirections();
  }, [appointments, assigneeAddress, generateGoogleDirections]);

  const getBackgroundColor = (stop: ITimelineWaypoint) => {
    if (stop.isHome || !stop.appointmentDetail) {
      return '#374151';
    }

    return getAppointmentTypeColor(stop.appointmentDetail.type);
  };

  const getAppointmentAddressName = (detail: ITimelinePreviewDialogAppointment) => {
    switch (detail.type) {
      case AppointmentType.SHOP_FITTING:
      case AppointmentType.SHOP_SERVICE:
      case AppointmentType.SHOP_TEST:
      case AppointmentType.SHOP_WAX_REMOVAL:
        return '45 Brunel Parkway, Pride Park, Derby, DE24 8HR';
      default:
        return createAddressString([...detail.address.formatted_address, detail.address.postcode]);
    }
  };
  const customContent = () => {
    return loading ? (
      <p className='p-4'>Loading timeline...</p>
    ) : (
      <div className='overflow-y-auto'>
        <RouteMap
          directions={{
            result: directionResult,
            pins: appointments.map((appointment, index) => ({
              position: getAppointmentAddressLocation(appointment).location,
              key: appointment.uid,
              iconUrl: getAppointmentTypePin(appointment.type),
              labelText: (index + 1).toString(),
            })),
          }}
        />
        <div className='p-4'>
          <div className='pb-4 body-02 opacity-80'>{`Total travel distance is ${totalMilage}`}</div>
          <div className='relative'>
            <div className='absolute w-[6px] ml-[12px] h-full rounded-full bg-blue-50' />
            {timeline?.map((stop, index) => (
              <div key={index}>
                <div className='flex items-center'>
                  <div
                    className='h-[30px] flex-grow-0 flex-shrink-0 basis-[30px] rounded-full z-20 flex items-center justify-center text-white font-semibold'
                    style={{ backgroundColor: getBackgroundColor(stop) }}
                  >
                    {!stop.isHome && <p>{index}</p>}
                  </div>
                  <div
                    className='ml-4 w-full rounded-md p-2 text-white'
                    style={{ backgroundColor: getBackgroundColor(stop) }}
                  >
                    {stop.isHome && <p>Audiologist home</p>}
                    {stop.appointmentDetail && (
                      <div className='body-02'>
                        <div className='flex justify-between'>
                          <p className='font-semibold'>{stop.appointmentDetail.customerName}</p>
                          <p className='opacity-80'>
                            {stop.appointmentDetail.type === 'new'
                              ? 'New'
                              : getAppointmentTypeNameFromKey(stop.appointmentDetail.type)}
                          </p>
                        </div>
                        <p className='body-03'>
                          {stop.appointmentDetail.start} - {stop.appointmentDetail.end}
                        </p>
                        <p>{getAppointmentAddressName(stop.appointmentDetail)}</p>
                      </div>
                    )}
                  </div>
                </div>
                {stop.nextJourney && (
                  <div className='ml-12 py-4 body-02 opacity-80'>
                    {`Travel is ${stop.nextJourney.distance?.text} and takes ${stop.nextJourney.duration?.text}`}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return <SharedDialogBase title='Timeline' customContentTemplate={customContent()} />;
};

export default TimelinePreviewDialog;
